import { effect, Injectable, signal } from '@angular/core';
import { IdentityService } from '../api/services/identity.service';
import { Observable } from 'rxjs';
import { StrictHttpResponse } from '../api/strict-http-response';
import { TokenModel } from '../api/models/token-model';
import { HttpHeaders } from '@angular/common/http';
import { environment } from '../environments/environment';
import { JwtHelperService } from '@auth0/angular-jwt';

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  activeAccountGuid = signal<string>('');
  private readonly jwtHelper = new JwtHelperService();

  readonly accessTokenLocalStorageKey = 'accessToken';
  readonly refreshTokenLocalStorageKey = 'refreshToken';
  readonly userGuidLocalStorageKey = 'kekenoQuestionnaireUserGuid';
  readonly accountGuidLocalStorageKey = 'accountGuid';

  constructor(private identityService: IdentityService) {
    this.activeAccountGuid.set(
      localStorage.getItem(this.accountGuidLocalStorageKey) ?? '',
    );
    effect(() => {
      const accountGuid = this.activeAccountGuid();
      if (accountGuid) {
        localStorage.setItem(this.accountGuidLocalStorageKey, accountGuid);
      } else {
        localStorage.removeItem(this.accountGuidLocalStorageKey);
      }
    });
  }

  /*
    Sends a request to /identity/authenticate using the provided credentials, and returns an observable containing
    the response from the API request
   */
  login(
    email: string,
    password: string,
    twoFactorAuthenticationCode?: string,
  ): Observable<StrictHttpResponse<TokenModel>> {
    return this.identityService.authenticatePost$Response({
      body: {
        email,
        password,
        twoFactorAuthenticationCode,
      },
    });
  }

  refreshToken(): Observable<StrictHttpResponse<TokenModel>> {
    return this.identityService.refreshPost$Response({
      body: {
        refresh: localStorage.getItem(this.refreshTokenLocalStorageKey)!,
      },
    });
  }

  /**
   * Logs user out, by clearing auth-related local storage properties
   */
  logout() {
    if (localStorage.getItem(this.refreshTokenLocalStorageKey)) {
      this.identityService
        .logoutPost$Response({
          body: {
            refreshToken: localStorage.getItem(
              this.refreshTokenLocalStorageKey,
            )!,
          },
        })
        .subscribe();
    }

    this.clearAuthState();
  }

  clearAuthState(): void {
    localStorage.removeItem(this.accessTokenLocalStorageKey);
    localStorage.removeItem(this.refreshTokenLocalStorageKey);
    localStorage.removeItem(this.userGuidLocalStorageKey);
    localStorage.removeItem(this.accountGuidLocalStorageKey);
    this.activeAccountGuid.set('');
  }

  /**
   Saves the tokens returned from an /authenticate or /refresh request to local storage, where they will then be
   used to authenticate requests
   */
  setTokens(model: TokenModel) {
    localStorage.setItem(this.accessTokenLocalStorageKey, model.accessToken);
    localStorage.setItem(this.refreshTokenLocalStorageKey, model.refreshToken);
    localStorage.setItem(this.userGuidLocalStorageKey, model.userGuid);
    this.setActiveAccountGuid(model.selectedAccountGuid);
  }

  /**
   * Gets the Guid of the currently logged-in user
   */
  getUserGuid() {
    return localStorage.getItem(this.userGuidLocalStorageKey);
  }

  getActiveAccountGuid(): string | null {
    return this.activeAccountGuid() || null;
  }

  setActiveAccountGuid(guid: string | null | undefined): void {
    this.activeAccountGuid.set(guid ?? '');
  }

  /*
    Get the headers required to use JWT Bearer auth against an API endpoint
   */
  getAuthorisationHeaders(existingHeaders?: HttpHeaders): HttpHeaders {
    const accessToken = localStorage.getItem(this.accessTokenLocalStorageKey);
    let headers =
      existingHeaders ??
      new HttpHeaders().set('Content-Type', 'application/json');

    if (accessToken) {
      headers = headers.set('Authorization', `Bearer ${accessToken}`);
    }

    return headers;
  }

  getApiKeyHeaders(existingHeaders?: HttpHeaders): HttpHeaders {
    if (existingHeaders) {
      return existingHeaders.set('X-Api-Key', environment.apiKey);
    } else {
      return new HttpHeaders()
        .set('Content-Type', 'application/json')
        .set('X-Api-Key', environment.apiKey);
    }
  }

  getTokenRoles(): string[] {
    const accessToken = localStorage.getItem(this.accessTokenLocalStorageKey);
    if (!accessToken) {
      return [];
    }

    const decodedToken = this.jwtHelper.decodeToken(accessToken) as Record<
      string,
      unknown
    > | null;
    if (!decodedToken) {
      return [];
    }

    const candidateClaims = [
      decodedToken['Role'],
      decodedToken['role'],
      decodedToken['roles'],
      decodedToken[
        'http://schemas.microsoft.com/ws/2008/06/identity/claims/role'
      ],
    ];

    const roles = candidateClaims.flatMap((claim) => {
      if (Array.isArray(claim)) {
        return claim.filter(
          (value): value is string => typeof value === 'string',
        );
      }
      if (typeof claim === 'string') {
        return [claim];
      }
      return [];
    });

    return [...new Set(roles)];
  }

  hasAnyRole(roles: string[]): boolean {
    const tokenRoles = this.getTokenRoles();
    return roles.some((role) => tokenRoles.includes(role));
  }
}
